import React from "react";
import { plan2plateIcons } from "../../utils/iconUtils";
import { cn } from "../../lib/utils";

type Size = "xs" | "sm" | "md" | "lg";
type Variant = "ghost" | "primary" | "secondary" | "tertiary" | "destructive";

const sizeClasses: Record<Size, string> = {
  xs: "h-6 w-6",
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const glyphSizes: Record<Size, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
};

const variantClasses: Record<Variant, string> = {
  ghost: "bg-surface-bg text-ink hover:bg-black/5 dark:hover:bg-white/10",
  primary: "bg-teal text-white hover:brightness-110 active:brightness-95",
  secondary: "bg-gold text-white hover:brightness-110 active:brightness-95",
  tertiary:
    "border border-line bg-surface-card text-ink-muted hover:bg-surface",
  destructive: "bg-transparent text-protein hover:bg-protein/10",
};

export type Plan2PlateButtonBaseProps = {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: Variant;
  isLoading?: boolean;
  "data-cy"?: string;
};

export interface Plan2PlateIconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: keyof typeof plan2plateIcons;
  size?: Size;
  variant?: Variant;
  isLoading?: boolean;
  isDisabled?: boolean;
  backgroundColor?: string;
  position?: React.CSSProperties["position"];
  top?: string | number;
  left?: string | number;
  zIndex?: string | number;
  "data-cy"?: string;
}

export const Plan2PlateIconButton = React.forwardRef<
  HTMLButtonElement,
  Plan2PlateIconButtonProps
>((props, ref) => {
  const {
    icon,
    size = "md",
    variant = "ghost",
    isLoading = false,
    isDisabled = false,
    backgroundColor,
    position,
    top,
    left,
    zIndex,
    className,
    style,
    type = "button",
    "data-cy": dataCy,
    ...rest
  } = props;

  const { stroke, ...restStyle } = style || {};

  const workarounds =
    icon == "barcode" ? "[&_path]:!fill-[var(--p2p-text)]" : "";

  return (
    <button
      ref={ref}
      type={type}
      data-cy={dataCy}
      disabled={isDisabled || isLoading}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-control transition",
        "disabled:cursor-not-allowed disabled:opacity-40",
        sizeClasses[size],
        variantClasses[variant],
        className,
        workarounds,
      )}
      style={{
        ...restStyle,
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(position ? { position } : {}),
        ...(top !== undefined ? { top } : {}),
        ...(left !== undefined ? { left } : {}),
        ...(zIndex !== undefined ? { zIndex } : {}),
      }}
      {...rest}
    >
      {isLoading ? (
        <span className="block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <span
          aria-hidden
          className="p2p-svg inline-flex shrink-0 items-center justify-center"
          style={{
            width: glyphSizes[size],
            height: glyphSizes[size],
            ...(stroke
              ? ({ "--p2p-text": stroke } as React.CSSProperties)
              : {}),
          }}
        >
          {plan2plateIcons[icon]}
        </span>
      )}
    </button>
  );
});
Plan2PlateIconButton.displayName = "Plan2PlateIconButton";
